import React, { useEffect, useLayoutEffect, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { createPortal } from 'react-dom';
import RequiredMark from '@/shein-platform/components/RequiredMark';
import CustomAttributeValueModal from '@/shein-platform/modules/products/components/CustomAttributeValueModal';
import SearchableSelect from '@/shein-platform/modules/products/components/SearchableSelect';
import { attributeDisplayLabel } from '@/shein-platform/utils/sheinAttributeTemplate';

function optionLabel(v) {
  return v?.nameCn || v?.name || '';
}

/** 紧凑多选下拉：可输入搜索并勾选；面板挂到 body，避免被弹窗裁切 */
function MultiSelectInput({
  options = [],
  value = [],
  onChange,
  placeholder = '',
  maxSelect = 0,
  disabled = false,
}) {
  const { t } = useTranslation('workbench');
  const rootRef = useRef(null);
  const controlRef = useRef(null);
  const panelRef = useRef(null);
  const [open, setOpen] = useState(false);
  const [keyword, setKeyword] = useState('');
  const [panelStyle, setPanelStyle] = useState({});
  const selected = useMemo(
    () => (Array.isArray(value) ? value : (value ? [value] : [])),
    [value],
  );

  const selectedOptions = useMemo(
    () => options.filter((opt) => selected.includes(opt.id)),
    [options, selected],
  );

  const filtered = useMemo(() => {
    const withLabel = (options || []).filter((opt) => String(optionLabel(opt) || '').trim());
    const q = keyword.trim().toLowerCase();
    if (!q) return withLabel;
    return withLabel.filter((opt) => optionLabel(opt).toLowerCase().includes(q));
  }, [options, keyword]);

  const updatePanelPosition = () => {
    const el = controlRef.current;
    if (!el) return;
    const rect = el.getBoundingClientRect();
    const maxHeight = Math.min(280, Math.max(140, window.innerHeight - rect.bottom - 12));
    setPanelStyle({
      position: 'fixed',
      top: rect.bottom + 4,
      left: rect.left,
      width: Math.max(rect.width, 240),
      maxHeight,
      zIndex: 15000,
    });
  };

  useLayoutEffect(() => {
    if (!open) return undefined;
    updatePanelPosition();
    const onReposition = () => updatePanelPosition();
    window.addEventListener('resize', onReposition);
    window.addEventListener('scroll', onReposition, true);
    return () => {
      window.removeEventListener('resize', onReposition);
      window.removeEventListener('scroll', onReposition, true);
    };
  }, [open, filtered.length, selected.length]);

  useEffect(() => {
    if (!open) return undefined;
    const onDoc = (event) => {
      const inRoot = rootRef.current?.contains(event.target);
      const inPanel = panelRef.current?.contains(event.target);
      if (!inRoot && !inPanel) {
        setOpen(false);
        setKeyword('');
      }
    };
    document.addEventListener('mousedown', onDoc);
    return () => document.removeEventListener('mousedown', onDoc);
  }, [open]);

  const toggle = (id) => {
    const set = new Set(selected);
    if (set.has(id)) set.delete(id);
    else {
      if (maxSelect > 0 && set.size >= maxSelect) return;
      set.add(id);
    }
    onChange([...set]);
  };

  const removeOne = (id, event) => {
    event.stopPropagation();
    onChange(selected.filter((item) => item !== id));
  };

  const panel = open && !disabled ? createPortal(
    <div
      ref={panelRef}
      className="spo-msel__panel spo-msel__panel--portal"
      style={panelStyle}
    >
      <div className="spo-msel__list">
        {filtered.length === 0 ? (
          <div className="spo-msel__empty">{t('productCopy.emptyOptions')}</div>
        ) : filtered.map((opt) => {
          const checked = selected.includes(opt.id);
          const locked = !checked && maxSelect > 0 && selected.length >= maxSelect;
          return (
            <label
              key={opt.id}
              className={`spo-msel__option${checked ? ' is-on' : ''}${locked ? ' is-locked' : ''}`}
            >
              <input
                type="checkbox"
                checked={checked}
                disabled={locked}
                onChange={() => toggle(opt.id)}
              />
              <span>{optionLabel(opt)}</span>
            </label>
          );
        })}
      </div>
      {selected.length > 0 ? (
        <div className="spo-msel__footer">
          <button type="button" className="spo-btn spo-btn--ghost spo-btn--sm" onClick={() => onChange([])}>
            {t('publishAttr.clear')}
          </button>
          <button
            type="button"
            className="spo-btn spo-btn--secondary spo-btn--sm"
            onClick={() => {
              setOpen(false);
              setKeyword('');
            }}
          >
            {t('returnList.completed')}
          </button>
        </div>
      ) : null}
    </div>,
    document.body,
  ) : null;

  return (
    <div className={`spo-msel${open ? ' is-open' : ''}${disabled ? ' is-disabled' : ''}`} ref={rootRef}>
      <div className="spo-msel__row">
        {selectedOptions.length > 0 ? (
          <div className="spo-msel__chips">
            {selectedOptions.map((opt) => (
              <span key={opt.id} className="spo-msel__chip">
                <em>{optionLabel(opt)}</em>
                <i
                  role="button"
                  tabIndex={0}
                  aria-label={t('productCopy.remove')}
                  onClick={(e) => removeOne(opt.id, e)}
                  onKeyDown={(e) => {
                    if (e.key === 'Enter' || e.key === ' ') removeOne(opt.id, e);
                  }}
                >
                  ×
                </i>
              </span>
            ))}
          </div>
        ) : null}
        <div
          className="spo-msel__control spo-msel__control--input"
          ref={controlRef}
          onClick={() => !disabled && setOpen(true)}
        >
          <input
            className="spo-msel__inline-input"
            value={keyword}
            disabled={disabled}
            placeholder={placeholder || t('publishAttr.pickOrSearch')}
            onFocus={() => !disabled && setOpen(true)}
            onChange={(e) => {
              setKeyword(e.target.value);
              setOpen(true);
            }}
            onKeyDown={(e) => {
              if (e.key === 'Enter') {
                e.preventDefault();
                const first = filtered.find((opt) => !selected.includes(opt.id));
                if (first) toggle(first.id);
                setKeyword('');
              }
              if (e.key === 'Escape') {
                setOpen(false);
                setKeyword('');
              }
            }}
            onClick={(e) => e.stopPropagation()}
          />
          <span className="spo-msel__caret" aria-hidden>▾</span>
        </div>
      </div>
      {panel}
    </div>
  );
}

function CustomValueHint({ visible, onClick }) {
  const { t } = useTranslation('workbench');
  if (!visible) return null;
  return (
    <div className="spo-attr-custom-hint">
      <span>{t('publishAttr.noValue')}</span>
      <button type="button" className="spo-attr-custom-hint__link" onClick={onClick}>
        {t('publishAttr.applyNew')}
      </button>
    </div>
  );
}

/** 属性库版本冲突：在对应选择框下方展示「最新」值（选择框/标签样式） */
function AttributeLatestValueHint({ hint }) {
  const { t } = useTranslation('workbench');
  if (!hint) return null;
  const parts = Array.isArray(hint.afterParts) && hint.afterParts.length
    ? hint.afterParts
    : (hint.after ? [String(hint.after)] : []);
  const isEmpty = !parts.length
    || (parts.length === 1 && (parts[0] === '（空）' || !String(parts[0]).trim()));
  return (
    <div className="spo-attr-latest-hint" role="note">
      <span className="spo-attr-latest-hint__badge">{t('publishAttr.latest')}</span>
      <div
        className="spo-attr-latest-hint__box"
        title={hint.after || ''}
      >
        {isEmpty ? (
          <span className="spo-attr-latest-hint__empty">{t('publishAttr.emptyMark')}</span>
        ) : (
          <div className="spo-msel__chips">
            {parts.map((text, index) => (
              <span
                key={`${String(text)}-${index}`}
                className="spo-msel__chip spo-attr-latest-hint__chip"
              >
                <em>{text}</em>
              </span>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}

/**
 * 类目属性控件：显示属性名称；下拉均可搜索。
 * allowCustomValue：平台 has_permission=1 时展示申请新增入口。
 */
export default function AttributeField({
  attr,
  value,
  onChange,
  required = false,
  shopId = '',
  categoryId = '',
  allowCustomValue = false,
  onCustomValueAdded,
  /** 独占整行（成分属性 / 末行落单） */
  fullWidth = false,
  /** 关联规则限制的属性值 id 合集；有值时过滤下拉 */
  allowedValueIds = null,
  /** 属性库版本冲突时的最新值提示 { after, afterParts } */
  versionUpdateHint = null,
}) {
  const { t } = useTranslation('workbench');
  const [extraValues, setExtraValues] = useState([]);
  const [customOpen, setCustomOpen] = useState(false);

  useEffect(() => {
    setExtraValues([]);
    setCustomOpen(false);
  }, [attr?.attributeId, categoryId]);

  const allowedSet = useMemo(() => {
    if (!Array.isArray(allowedValueIds) || !allowedValueIds.length) return null;
    return new Set(allowedValueIds.map(String));
  }, [allowedValueIds]);

  const mergedValues = useMemo(() => {
    const base = Array.isArray(attr?.values) ? attr.values : [];
    const seen = new Set(base.map((v) => String(v.id)));
    const extras = extraValues.filter((v) => v?.id && !seen.has(String(v.id)));
    const all = extras.length ? [...base, ...extras] : base;
    if (!allowedSet) return all;
    return all.filter((v) => v?.id && allowedSet.has(String(v.id)));
  }, [attr?.values, extraValues, allowedSet]);

  if (!attr) return null;
  const label = attributeDisplayLabel(attr);

  const selectOptions = mergedValues
    .filter((v) => v?.id && String(optionLabel(v) || '').trim())
    .map((v) => ({
      value: v.id,
      label: optionLabel(v),
    }));

  const showCustomHint = Boolean(allowCustomValue && shopId && categoryId && attr.attributeId);
  const canCustom = showCustomHint && attr.mode !== 0;

  const applyNewOption = (opt) => {
    if (!opt?.id) return;
    setExtraValues((prev) => (
      prev.some((item) => String(item.id) === String(opt.id))
        ? prev
        : [...prev, { id: String(opt.id), name: opt.name, nameCn: opt.nameCn || opt.name }]
    ));
    onCustomValueAdded?.(attr.attributeId, opt);

    if (attr.mode === 4 || attr.isComposition) {
      let rows = [];
      if (Array.isArray(value)) {
        rows = value.map((row) => {
          if (row && typeof row === 'object') {
            const valueId = row.valueId ?? row.attributeValueId;
            return {
              valueId: valueId == null || valueId === '' ? '' : String(valueId),
              percent: String(row.percent ?? row.attributeExtraValue ?? ''),
            };
          }
          if (row == null || row === '') return { valueId: '', percent: '' };
          return { valueId: String(row), percent: '' };
        });
      } else if (value != null && value !== '') {
        rows = [{ valueId: String(value), percent: '' }];
      }
      const displayRows = rows.length ? rows : [{ valueId: '', percent: '' }];
      const emptyIdx = displayRows.findIndex((row) => !row.valueId);
      if (emptyIdx >= 0) {
        onChange(displayRows.map((row, i) => (
          i === emptyIdx ? { ...row, valueId: String(opt.id) } : row
        )));
      } else {
        onChange([...displayRows, { valueId: String(opt.id), percent: '' }]);
      }
      return;
    }
    if (attr.mode === 1) {
      const selected = Array.isArray(value) ? value.map(String) : (value ? [String(value)] : []);
      if (!selected.includes(String(opt.id))) onChange([...selected, String(opt.id)]);
      return;
    }
    onChange(String(opt.id));
  };

  const customModal = (
    <CustomAttributeValueModal
      open={customOpen}
      shopId={shopId}
      categoryId={categoryId}
      attributeId={attr.attributeId}
      attributeLabel={label}
      onClose={() => setCustomOpen(false)}
      onAdded={applyNewOption}
    />
  );

  if (attr.mode === 4 || attr.isComposition) {
    // 兼容回填：string / string[] / [{valueId,percent}]
    // 空 valueId 行必须保留，否则「添加」写入的新行会被滤掉，表现为无反应
    let rows = [];
    if (Array.isArray(value)) {
      rows = value.map((row) => {
        if (row && typeof row === 'object') {
          const valueId = row.valueId ?? row.attributeValueId;
          return {
            valueId: valueId == null || valueId === '' ? '' : String(valueId),
            percent: String(row.percent ?? row.attributeExtraValue ?? ''),
          };
        }
        if (row == null || row === '') {
          return { valueId: '', percent: '' };
        }
        return { valueId: String(row), percent: '' };
      });
    } else if (value != null && value !== '' && (typeof value === 'string' || typeof value === 'number')) {
      rows = [{ valueId: String(value), percent: '' }];
    }
    const displayRows = rows.length ? rows : [{ valueId: '', percent: '' }];
    const total = displayRows.reduce((sum, row) => sum + (Number(row.percent) || 0), 0);
    const usedIds = new Set(displayRows.map((row) => row.valueId).filter(Boolean));
    const updateRow = (index, patch) => {
      const next = displayRows.map((row, i) => (i === index ? { ...row, ...patch } : row));
      onChange(next);
    };
    const addRow = () => onChange([...displayRows, { valueId: '', percent: '' }]);
    const removeRow = (index) => {
      const next = displayRows.filter((_, i) => i !== index);
      onChange(next.length ? next : [{ valueId: '', percent: '' }]);
    };

    return (
      <div className="spo-field spo-field--full spo-attr-comp">
        <span className="spo-field__label">
          {label}{required ? <RequiredMark /> : null}
          <span className={`spo-attr-comp__total${total === 100 ? ' is-ok' : ''}`}>{t('publishAttr.total', { total })}</span>
        </span>
        <div className="spo-attr-comp__rows">
          {displayRows.map((row, index) => (
            <div key={`${attr.attributeId}-${index}`} className="spo-attr-comp__row">
              <SearchableSelect
                options={selectOptions.map((opt) => ({
                  ...opt,
                  disabled: usedIds.has(opt.value) && row.valueId !== opt.value,
                }))}
                value={row.valueId || ''}
                onChange={(next) => updateRow(index, { valueId: next })}
                placeholder={t('publishAttr.pickOrSearch')}
              />
              <div className="spo-attr-comp__pct">
                <input
                  className="spo-input"
                  inputMode="numeric"
                  value={row.percent ?? ''}
                  onChange={(e) => updateRow(index, { percent: e.target.value.replace(/[^\d.]/g, '') })}
                  placeholder={t('publishAttr.percent')}
                />
                <span>%</span>
              </div>
              {displayRows.length > 1 ? (
                <button type="button" className="spo-btn spo-btn--ghost spo-btn--sm" onClick={() => removeRow(index)}>
                  {t('shop.delete')}
                </button>
              ) : <span />}
            </div>
          ))}
        </div>
        <button type="button" className="spo-btn spo-btn--secondary spo-btn--sm" onClick={addRow}>
          {t('publishAttr.add')}
        </button>
        <AttributeLatestValueHint hint={versionUpdateHint} />
        <CustomValueHint visible={canCustom} onClick={() => setCustomOpen(true)} />
        {customModal}
      </div>
    );
  }

  const fieldClass = `spo-field${fullWidth ? ' spo-field--full' : ''}`;

  if (attr.mode === 1) {
    const selected = Array.isArray(value) ? value : (value ? [value] : []);
    return (
      <div className={fieldClass}>
        <span className="spo-field__label">{label}{required ? <RequiredMark /> : null}</span>
        <MultiSelectInput
          options={mergedValues}
          value={selected}
          maxSelect={attr.maxSelect || 0}
          onChange={onChange}
          placeholder={t('productCopy.searchDefault')}
        />
        <AttributeLatestValueHint hint={versionUpdateHint} />
        <CustomValueHint visible={canCustom} onClick={() => setCustomOpen(true)} />
        {customModal}
      </div>
    );
  }

  if (attr.mode === 0) {
    return (
      <label className={fieldClass}>
        <span className="spo-field__label">{label}{required ? <RequiredMark /> : null}</span>
        <input
          className="spo-input"
          value={value || ''}
          maxLength={100}
          onChange={(e) => onChange(e.target.value)}
          placeholder={t('publishAttr.input')}
        />
        <AttributeLatestValueHint hint={versionUpdateHint} />
      </label>
    );
  }

  return (
    <div className={fieldClass}>
      <span className="spo-field__label">{label}{required ? <RequiredMark /> : null}</span>
      <SearchableSelect
        options={selectOptions}
        value={value || ''}
        onChange={onChange}
        placeholder={t('publishAttr.pickOrSearch')}
      />
      <AttributeLatestValueHint hint={versionUpdateHint} />
      <CustomValueHint visible={canCustom} onClick={() => setCustomOpen(true)} />
      {customModal}
    </div>
  );
}
